import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Loader2, MapPin } from "lucide-react";

const SUGGEST = "https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest";
const LOOKUP = "https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup";

export default function AddressAutocomplete({ onSelect, label = "Zoek Nederlands adres (autocomplete)" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 3) { setResults([]); setOpen(false); return; }
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(`${SUGGEST}?q=${encodeURIComponent(q)}&fq=type:adres&rows=6`);
        const d = await r.json();
        setResults(d?.response?.docs || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = async (doc) => {
    setQ(doc.weergavenaam);
    setOpen(false);
    try {
      const r = await fetch(`${LOOKUP}?id=${doc.id}&fl=straatnaam,huis_nlt,postcode,woonplaatsnaam`);
      const d = await r.json();
      const a = d?.response?.docs?.[0] || {};
      const street = a.straatnaam || "";
      const number = a.huis_nlt || "";
      const postal_code = a.postcode || "";
      const city = a.woonplaatsnaam || "";
      const full = `${street} ${number}`.trim() + (postal_code || city ? `, ${postal_code} ${city}`.trimEnd() : "");
      onSelect({ street, number, postal_code, city, full });
    } catch {}
  };

  return (
    <div className="relative" ref={boxRef}>
      <Label>{label}</Label>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="address-autocomplete-input"
          className="pl-9"
          placeholder="Bijv. postcode + huisnr (1012NX 1) of straatnaam"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          autoComplete="off"
        />
        {loading && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="address-suggestions">
          {results.map((doc) => (
            <button
              key={doc.id}
              type="button"
              data-testid={`address-suggestion-${doc.id}`}
              onClick={() => pick(doc)}
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 last:border-0"
            >
              <MapPin className="h-4 w-4 text-emerald-600 shrink-0" />
              <span className="text-slate-700">{doc.weergavenaam}</span>
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-1">Gegevens via PDOK (Kadaster). Selecteer een adres om de velden automatisch te vullen.</p>
    </div>
  );
}
